import { buildUrl } from "./client";
import { REALTIME_EVENT_TYPES, type RealtimeEnvelope } from "./types";

export type RealtimeStatus = "connecting" | "open" | "reconnecting" | "offline";

export interface RealtimeSubscription {
  storeId: string | null;
  onEvent: (event: RealtimeEnvelope) => void;
  onStatus: (status: RealtimeStatus) => void;
  /** Called after a reconnect so the app can refetch anything it may have missed. */
  onResync: () => void;
}

export interface RealtimeSource {
  connect(sub: RealtimeSubscription): () => void;
}

/**
 * SSE via the browser EventSource (cookies included). EventSource retries on network errors by
 * itself (sending Last-Event-ID, which the backend uses to replay missed events). When the stream
 * is closed for good (e.g. an HTTP error), we reconnect with exponential backoff and ask the app
 * to resync because replay is not possible from a fresh connection.
 */
export function createEventSourceRealtime(baseUrl: string): RealtimeSource {
  return {
    connect(sub) {
      let source: EventSource | null = null;
      let retryTimer: ReturnType<typeof setTimeout> | undefined;
      let attempt = 0;
      let disposed = false;
      let everOpened = false;

      const open = () => {
        if (disposed) return;
        sub.onStatus(everOpened ? "reconnecting" : "connecting");
        source = new EventSource(buildUrl(baseUrl, "/v1/events", { storeId: sub.storeId }), {
          withCredentials: true,
        });
        source.addEventListener("ready", () => {
          const resync = everOpened;
          everOpened = true;
          attempt = 0;
          sub.onStatus("open");
          if (resync) sub.onResync();
        });
        for (const type of REALTIME_EVENT_TYPES) {
          source.addEventListener(type, (e) => {
            try {
              sub.onEvent(JSON.parse((e as MessageEvent<string>).data) as RealtimeEnvelope);
            } catch {
              /* malformed event: ignore */
            }
          });
        }
        source.onerror = () => {
          if (!source) return;
          if (source.readyState === EventSource.CLOSED) {
            source.close();
            source = null;
            sub.onStatus(navigator.onLine === false ? "offline" : "reconnecting");
            const delay = Math.min(30_000, 1000 * 2 ** attempt++);
            retryTimer = setTimeout(open, delay);
          } else {
            sub.onStatus("reconnecting"); // browser is retrying on its own
          }
        };
      };

      open();
      return () => {
        disposed = true;
        if (retryTimer) clearTimeout(retryTimer);
        source?.close();
      };
    },
  };
}
